import React, { useContext, forwardRef, useMemo } from "react";
import { Col } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import * as Icon from "react-bootstrap-icons";
import "./customelements.css";
import { FormContext } from "../FormContext";

/**
 * DateTime Component
 * 
 * A flexible date/datetime picker that supports:
 * - Date only mode (showTimeSelect=false)
 * - Date + time mode (showTimeSelect=true, default)
 * - Prop-driven usage (with onChange callback)
 * - Context-driven usage (with FormContext)
 * 
 * @param {string} id - Field identifier
 * @param {string} label - Field label
 * @param {string} placeholder - Placeholder text
 * @param {Date|null} value - Selected date value
 * @param {boolean} disabled - Whether the field is disabled
 * @param {boolean} hidden - Whether to hide the field
 * @param {string} example - Example text to display
 * @param {boolean} mandatory - Whether the field is required
 * @param {number} md - Bootstrap column width
 * @param {function} onChange - Callback when date changes (prop-driven usage)
 * @param {boolean} showTimeSelect - Whether to show time picker (default: false)
 * @param {string} dateFormat - Custom date format (optional)
 */
const DateTime = ({
  id,
  label,
  placeholder,
  value,
  disabled,
  hidden,
  example,
  mandatory,
  md,
  onChange,
  showTimeSelect = false, // Default to date-only for reports
  dateFormat, // Optional custom format
}) => {
  const ctx = useContext(FormContext);

  // Determine the date format based on whether time is shown
  const displayFormat = useMemo(() => {
    if (dateFormat) return dateFormat;
    return showTimeSelect ? "MMMM d, yyyy h:mm aa" : "MM/dd/yyyy";
  }, [dateFormat, showTimeSelect]);

  // Determine placeholder based on mode
  const displayPlaceholder = useMemo(() => {
    if (placeholder) return placeholder;
    return showTimeSelect ? "Select date and time" : "Select date";
  }, [placeholder, showTimeSelect]);

  const handleDateChange = (date) => {
    // If parent passed onChange, use it (FlowReport use-case)
    if (typeof onChange === "function") {
      onChange(date);
      return;
    }

    // Otherwise fallback to legacy FormContext behavior
    if (ctx && typeof ctx.handleChange === "function") {
      ctx.handleChange(id, date);
    }
  };

  const DateCustomInput = useMemo(
    () =>
      forwardRef(({ value, onClick, onChange }, ref) => (
        <>
          <input
            value={value}
            placeholder={displayPlaceholder}
            className="date-custom-input"
            onClick={onClick}
            onChange={onChange}
            ref={ref}
            disabled={disabled}
            required={mandatory}
            autoComplete="new-password"
          />
          <Icon.Calendar2WeekFill className="calendar-icon-custom-input" />
        </>
      )),
    [disabled, mandatory, displayPlaceholder]
  );

  if (hidden) return null;

  return (
    <Col md={md}>
      <div className="mb-3">
        {label ? (
          <label htmlFor={id} className="form-label move-left">
            {mandatory ? (
              <span style={{ color: "red", marginRight: 5 }}>*</span>
            ) : null}
            {label}
          </label>
        ) : null}

        <DatePicker
          id={id}
          showIcon={true}
          selected={value || null}
          placeholderText={displayPlaceholder}
          onChange={handleDateChange}
          customInput={<DateCustomInput />}
          dayClassName={() => "custom-datepicker-day-class"}
          popperClassName="custom-datepicker-class"
          showTimeSelect={showTimeSelect}
          timeFormat="HH:mm"
          timeIntervals={15}
          timeCaption="time"
          dateFormat={displayFormat}
          disabled={disabled}
          isClearable={!disabled && !mandatory}
        />

        {example !== null && example !== undefined ? (
          <div id="emailHelp" className="text-muted move-left input-example">
            Example: {example}
          </div>
        ) : null}
      </div>
    </Col>
  );
};

export default DateTime;